import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  Briefcase, DollarSign, Clock, User, Mail, Phone, Building2,
  Globe, CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp,
  RefreshCw, Calendar, FileText, ExternalLink, Calculator
} from "lucide-react";
import { QuoteBuilder } from "./QuoteBuilder";

const PROJECT_TYPE_LABELS = {
  business_setup: "Business Setup & Formation",
  brand_design: "Brand & Graphic Design",
  sop_creation: "SOP & Process Documentation",
  website: "Website Development",
  marketing: "Marketing Strategy",
  social_media: "Social Media Management",
  seo: "SEO & Content",
  admin_support: "Admin & Operations Support",
  media_promo: "Media & Podcast Production",
  consulting: "Strategic Consulting",
  other: "Other"
};

const TIMELINE_LABELS = {
  urgent: "ASAP (Within 1 week)",
  short: "Short-term (2-4 weeks)",
  medium: "Medium-term (1-2 months)",
  long: "Long-term (3+ months)",
  ongoing: "Ongoing/Retainer",
  flexible: "Flexible"
};

const BUDGET_LABELS = {
  under_500: "Under $500",
  "500_1000": "$500 - $1,000",
  "1000_2500": "$1,000 - $2,500",
  "2500_5000": "$2,500 - $5,000",
  "5000_10000": "$5,000 - $10,000",
  "10000_plus": "$10,000+",
  discuss: "To be discussed"
};

export const ProjectScopePanel = ({ open, onOpenChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [quoteRequest, setQuoteRequest] = useState(null);

  useEffect(() => {
    if (open) {
      fetchRequests();
    }
  }, [open]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/project-scope-requests`);
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (error) {
      console.error("Error fetching project scope requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest || !newStatus) return;
    
    setUpdating(true);
    try {
      const res = await authFetch(
        `${API}/consultant/project-scope-requests/${selectedRequest.request_id}/status?status=${newStatus}&notes=${encodeURIComponent(statusNotes)}`,
        { method: "PUT" }
      );
      
      if (res.ok) {
        toast.success("Status updated successfully");
        setSelectedRequest(null);
        setStatusNotes("");
        setNewStatus("");
        fetchRequests();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-700",
      reviewing: "bg-amber-100 text-amber-700",
      quoted: "bg-purple-100 text-purple-700",
      accepted: "bg-green-100 text-green-700",
      declined: "bg-red-100 text-red-700",
      completed: "bg-neutral-100 text-neutral-700"
    };
    return <Badge className={styles[status] || "bg-neutral-100"}>{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(req => {
    if (filter === "all") return true;
    return req.status === filter;
  });

  const newCount = requests.filter(r => r.status === "new").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-purple-600" />
            Project Scope Requests
            {newCount > 0 && (
              <Badge className="bg-blue-500 text-white ml-2">{newCount} new</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Review and respond to project scope requests from potential clients
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchRequests}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No project scope requests yet</p>
              <p className="text-sm text-neutral-400 mt-1">
                Requests from the pricing page will appear here
              </p>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <Card 
                key={request.request_id}
                className={`transition-colors ${
                  request.status === "new" 
                    ? "border-blue-200 bg-blue-50/50" 
                    : "border-neutral-200"
                }`}
              >
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-neutral-900">
                          {request.project_title || "Untitled Project"}
                        </h4>
                        {getStatusBadge(request.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-neutral-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {request.name}
                        </span>
                        {request.company_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {request.company_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(request.created_at)}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedRequest(
                        expandedRequest === request.request_id ? null : request.request_id
                      )}
                    >
                      {expandedRequest === request.request_id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>

                  {/* Quick Info */}
                  <div className="flex flex-wrap gap-3 mb-3">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {TIMELINE_LABELS[request.timeline] || request.timeline}
                    </Badge>
                    <Badge variant="outline" className="flex items-center gap-1 text-green-700 border-green-300">
                      <DollarSign className="w-3 h-3" />
                      {BUDGET_LABELS[request.budget_range] || request.budget_range}
                    </Badge>
                    {request.project_types?.map((type, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {PROJECT_TYPE_LABELS[type] || type}
                      </Badge>
                    ))}
                  </div>

                  {/* Expanded Details */}
                  {expandedRequest === request.request_id && (
                    <div className="mt-4 pt-4 border-t border-neutral-200 space-y-4">
                      {/* Contact Info */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-neutral-50 p-3 rounded-lg">
                          <h5 className="font-medium text-sm text-neutral-700 mb-2">Contact Information</h5>
                          <div className="space-y-1 text-sm">
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-neutral-400" />
                              <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline">
                                {request.email}
                              </a>
                            </p>
                            {request.phone && (
                              <p className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-neutral-400" />
                                {request.phone}
                              </p>
                            )}
                            {request.website && (
                              <p className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-neutral-400" />
                                <a href={request.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                  {request.website}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="bg-neutral-50 p-3 rounded-lg">
                          <h5 className="font-medium text-sm text-neutral-700 mb-2">Project Timeline</h5>
                          <div className="space-y-1 text-sm">
                            <p><strong>Timeline:</strong> {TIMELINE_LABELS[request.timeline] || request.timeline}</p>
                            <p><strong>Budget:</strong> {BUDGET_LABELS[request.budget_range] || request.budget_range}</p>
                            {request.start_date && (
                              <p><strong>Start Date:</strong> {new Date(request.start_date).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Project Description */}
                      <div className="bg-white p-3 rounded-lg border">
                        <h5 className="font-medium text-sm text-neutral-700 mb-2">Project Description</h5>
                        <p className="text-sm text-neutral-600 whitespace-pre-wrap">
                          {request.project_description}
                        </p>
                      </div>

                      {/* Goals */}
                      {request.goals && (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                          <h5 className="font-medium text-sm text-green-700 mb-2">Success Criteria / Goals</h5>
                          <p className="text-sm text-green-800">{request.goals}</p>
                        </div>
                      )}

                      {/* Additional Info */}
                      {(request.competitors || request.additional_notes || request.has_existing_materials) && (
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                          <h5 className="font-medium text-sm text-purple-700 mb-2">Additional Information</h5>
                          <div className="space-y-2 text-sm text-purple-800">
                            {request.has_existing_materials && (
                              <p><strong>Has Materials:</strong> {request.existing_materials_description || "Yes"}</p>
                            )}
                            {request.competitors && (
                              <p><strong>Competitors/Inspiration:</strong> {request.competitors}</p>
                            )}
                            {request.additional_notes && (
                              <p><strong>Notes:</strong> {request.additional_notes}</p>
                            )}
                            {request.how_heard && (
                              <p><strong>Source:</strong> {request.how_heard}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Status Notes */}
                      {request.status_notes && (
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                          <h5 className="font-medium text-sm text-amber-700 mb-1">Internal Notes</h5>
                          <p className="text-sm text-amber-800">{request.status_notes}</p>
                          {request.status_updated_at && (
                            <p className="text-xs text-amber-600 mt-1">
                              Updated: {formatDate(request.status_updated_at)} by {request.status_updated_by}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Quote Info if sent */}
                      {request.quote_sent_at && (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-100 mb-3">
                          <h5 className="font-medium text-sm text-green-700 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Quote Sent
                          </h5>
                          <div className="text-sm text-green-800 space-y-1">
                            <p><strong>Amount:</strong> ${request.quote_amount?.toLocaleString()}</p>
                            <p><strong>Hours:</strong> {request.quote_hours} hrs @ ${request.quote_rate}/hr</p>
                            <p className="text-xs text-green-600">
                              Sent: {formatDate(request.quote_sent_at)} by {request.quote_sent_by}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-3 pt-2">
                        <Button
                          size="sm"
                          onClick={() => setQuoteRequest(request)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Calculator className="w-4 h-4 mr-1" />
                          {request.quote_sent_at ? "Resend Quote" : "Create Quote"}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setNewStatus(request.status);
                            setStatusNotes(request.status_notes || "");
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Update Status
                        </Button>
                        <a href={`mailto:${request.email}?subject=Re: Project Scope Request - ${request.project_title || request.company_name}`}>
                          <Button size="sm" variant="outline">
                            <Mail className="w-4 h-4 mr-1" />
                            Email Client
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Quote Builder Dialog */}
        <QuoteBuilder
          open={!!quoteRequest}
          onOpenChange={(open) => !open && setQuoteRequest(null)}
          request={quoteRequest}
          onQuoteSent={fetchRequests}
        />

        {/* Status Update Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Request Status</DialogTitle>
              <DialogDescription>
                Update the status for {selectedRequest?.name}'s project request
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="quoted">Quoted (Proposal Sent)</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Internal Notes</label>
                <Textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any internal notes about this request..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStatus}
                disabled={updating || !newStatus}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-1" />
                )}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
